package diana.soleil.design.iraniancanadienbazzar.core.data

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * Which phased features are live for this user right now. Everything defaults **on** (mirrors the
 * Remote Config template) — owner decision 2026-07-10: launch with every phase open. Remote Config
 * `feature_*_enabled` keys remain per-feature kill switches (see `docs/ROADMAP.md`), so a flag flip
 * can still turn any surface off without a store release.
 */
data class FeatureAvailability(
    val chat: Boolean = true,
    val feed: Boolean = true,
    val marketplace: Boolean = true,
    val ordering: Boolean = true,
    val events: Boolean = true,
    /** Hidden Gems hub (`feature_hiddengems_enabled`): gates the Home rail + `/gems` routes. */
    val hiddenGems: Boolean = true,
    /** Danestaniha section (`feature_danestaniha_enabled`): gates the hub card + `/gems/danestaniha`. */
    val danestaniha: Boolean = true,
    /** Shahnameh tales section (`feature_shahnameh_enabled`): gates the hub card + `/gems/shahnameh`. */
    val shahnameh: Boolean = true,
    /** Persian poetry section (`feature_poetry_enabled`): gates the hub card + `/gems/poetry`. */
    val poetry: Boolean = true,
    /** Short-stories section (`feature_stories_enabled`): gates the hub card + `/gems/stories`. */
    val stories: Boolean = true,
    /** Fal-e Hafez (`feature_fal_enabled`): gates the hub card + `/gems/fal`. */
    val fal: Boolean = true,
    /** Merchant deals/promotions (`feature_deals_enabled`): gates the Home rail + dashboard authoring. */
    val deals: Boolean = true,
    /** Find-your-people discovery (`feature_people_enabled`): gates the People surface. */
    val people: Boolean = true,
    /**
     * Stripe-backed paid surfaces (`feature_payments_enabled`), default **FALSE**: the dashboard
     * "Promote listing" sheet and the Profile "Mahaleh Pro" plan rows.
     *
     * Stripe is configured in **test mode only**. On an environment without prod Stripe secrets the
     * Checkout callables fail, so the buttons dead-end on a real user's card — the launch decision
     * (docs/GO_LIVE.md Option A) is to ship the directory free and turn payments on as a fast-follow
     * once prod keys, the live webhook and the Billing Portal are in place. Off also means the
     * surfaces are simply absent, not "coming soon" — no fake paywalls (docs/ROADMAP.md).
     */
    val payments: Boolean = false,
    /**
     * Home density (`home_dense_sections_enabled`): when **false** (the launch default) Home drops
     * the discretionary rails — For You, People, Deals, News — and keeps Categories, Featured, the
     * Events/Gems entry cards and Popular. Thin rails read as an empty app; flip this on once a
     * metro has the volume to fill them.
     */
    val homeDenseSections: Boolean = false,
)

/**
 * One individually-overridable flag inside [FeatureAvailability]. Exists so the debug Developer Menu
 * can enumerate and flip flags generically (golden rule 9 — a feature that needs a toggle to exercise
 * it registers one) instead of growing a bespoke `DevTool` per flag, which is how this started and
 * which only ever covered Hidden Gems.
 *
 * [read]/[write] stand in for reflection, which Kotlin/Common doesn't have.
 */
data class FeatureToggle(
    val id: String,
    /** The Remote Config key this shadows — surfaced in the dev menu so QA knows what they're faking. */
    val remoteConfigKey: String,
    /** Debug-only English label (docs/DEVELOPER_MENU.md grants the dev menu a copy dispensation). */
    val label: String,
    val read: (FeatureAvailability) -> Boolean,
    val write: (FeatureAvailability, Boolean) -> FeatureAvailability,
) {
    companion object {
        val ALL: List<FeatureToggle> = listOf(
            FeatureToggle("chat", "feature_chat_enabled", "Chat", { it.chat }) { a, v -> a.copy(chat = v) },
            FeatureToggle("feed", "feature_feed_enabled", "Community feed", { it.feed }) { a, v -> a.copy(feed = v) },
            FeatureToggle("marketplace", "feature_marketplace_enabled", "Marketplace", { it.marketplace }) { a, v -> a.copy(marketplace = v) },
            FeatureToggle("ordering", "feature_ordering_enabled", "Ordering", { it.ordering }) { a, v -> a.copy(ordering = v) },
            FeatureToggle("events", "feature_events_enabled", "Events", { it.events }) { a, v -> a.copy(events = v) },
            FeatureToggle("hiddenGems", "feature_hiddengems_enabled", "Hidden Gems hub", { it.hiddenGems }) { a, v -> a.copy(hiddenGems = v) },
            FeatureToggle("danestaniha", "feature_danestaniha_enabled", "Danestaniha", { it.danestaniha }) { a, v -> a.copy(danestaniha = v) },
            FeatureToggle("shahnameh", "feature_shahnameh_enabled", "Shahnameh tales", { it.shahnameh }) { a, v -> a.copy(shahnameh = v) },
            FeatureToggle("poetry", "feature_poetry_enabled", "Persian poetry", { it.poetry }) { a, v -> a.copy(poetry = v) },
            FeatureToggle("stories", "feature_stories_enabled", "Short stories", { it.stories }) { a, v -> a.copy(stories = v) },
            FeatureToggle("fal", "feature_fal_enabled", "Fal-e Hafez", { it.fal }) { a, v -> a.copy(fal = v) },
            FeatureToggle("deals", "feature_deals_enabled", "Deals", { it.deals }) { a, v -> a.copy(deals = v) },
            FeatureToggle("people", "feature_people_enabled", "Find your people", { it.people }) { a, v -> a.copy(people = v) },
            FeatureToggle("payments", "feature_payments_enabled", "Payments (Pro + Promote)", { it.payments }) { a, v -> a.copy(payments = v) },
            FeatureToggle("homeDenseSections", "home_dense_sections_enabled", "Dense Home rails", { it.homeDenseSections }) { a, v -> a.copy(homeDenseSections = v) },
        )
    }
}

/**
 * Holds the [FeatureAvailability] as observable state. Driven by Remote Config from the composition
 * root (same pattern as `CityController.setLaunchedMetros` / `SponsorshipConfig`, so feature modules
 * and the nav shell stay decoupled from `:core:firebase`). The bottom-nav shell derives its visible
 * tabs from this; feature entry points check their own flag.
 *
 * Debug overrides sit in a layer ON TOP of the fetched values rather than being written into them:
 * the Developer Menu's original approach — `update(available.value.copy(...))` — was silently undone
 * by the next Remote Config re-fetch (which happens on resume), so a QA override could evaporate
 * mid-flow. [setOverride] survives any number of fetches until it is explicitly cleared.
 */
class FeatureFlags {
    private var fetched = FeatureAvailability()
    private val overrides = mutableMapOf<String, Boolean>()
    private val _available = MutableStateFlow(fetched)
    val available: StateFlow<FeatureAvailability> = _available.asStateFlow()

    /** Pushed from the composition root after a Remote Config fetch. */
    fun update(availability: FeatureAvailability) {
        fetched = availability
        recompute()
    }

    /** Debug only. `null` clears the override and returns the flag to whatever Remote Config fetched. */
    fun setOverride(toggle: FeatureToggle, value: Boolean?) {
        if (value == null) overrides.remove(toggle.id) else overrides[toggle.id] = value
        recompute()
    }

    /** The active debug override for [toggle], or `null` when it is following Remote Config. */
    fun override(toggle: FeatureToggle): Boolean? = overrides[toggle.id]

    fun clearOverrides() {
        overrides.clear()
        recompute()
    }

    private fun recompute() {
        _available.value = FeatureToggle.ALL.fold(fetched) { acc, toggle ->
            overrides[toggle.id]?.let { toggle.write(acc, it) } ?: acc
        }
    }
}
